import React from 'react';
import { SIDEBAR_PIN_KEY } from './shellTokens';

/**
 * "Keep the rail open" preference, persisted per browser.
 *
 * Lives in AppLayout rather than inside the Sidebar because the pin changes the
 * LAYOUT width — the workspace and the AppBar both offset by it — whereas the
 * transient hover expansion deliberately does not (it overlays, so the page
 * never reflows under the pointer). Two different states, two different homes.
 *
 * Reads are wrapped because localStorage throws outright in Safari private mode
 * and in some embedded webviews; an unreadable preference must degrade to
 * "unpinned", never to a white screen.
 */
export const useSidebarPin = (): [boolean, () => void] => {
  const [pinned, setPinned] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_PIN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = React.useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_PIN_KEY, String(next));
      } catch {
        /* preference is a nicety — never let a storage failure break nav */
      }
      return next;
    });
  }, []);

  return [pinned, toggle];
};
